import { EmptyState } from '@/components/patterns/empty-state'
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart'
import { Typography } from '@/components/ui/typography'
import { readProperty } from '@/lib/utils'
import { Cell, Label, Pie, PieChart } from 'recharts'
import { formatPieSharePercent, renderPieShareLabel, ReportPieCenterLabel } from '../-helpers'
import type { JurisdictionChartRow } from './-presentation'

export function JurisdictionTaxPieChart({ chartRows }: { chartRows: JurisdictionChartRow[] }) {
  if (!chartRows.length) {
    return <EmptyState size="compact" surface="transparent" title="No tax collected to chart" />
  }

  const chartConfig = Object.fromEntries(
    chartRows.map((row) => [
      row.chartKey,
      {
        label: row.jurisdiction_label,
        color: row.fill,
      },
    ])
  )
  const totalTaxCollected = chartRows.reduce((sum, row) => sum + row.tax_value, 0)

  return (
    <div className="flex flex-col items-center justify-center">
      <ChartContainer config={chartConfig} className="aspect-square h-[240px] w-full max-w-[280px]">
        <PieChart>
          <ChartTooltip
            cursor={false}
            content={
              <ChartTooltipContent
                hideLabel
                formatter={(_value, _name, _item, _index, slice) => {
                  // Recharts hands back the hovered slice's data row untyped; find it by its key.
                  const row = chartRows.find(
                    (candidate) => candidate.chartKey === readProperty(slice, 'chartKey')
                  )

                  return row ? (
                    <div className="flex w-full items-center justify-between gap-3">
                      <span>{row.jurisdiction_label}</span>
                      <span className="font-medium tabular-nums text-foreground">
                        {formatPieSharePercent(row.share)}
                      </span>
                    </div>
                  ) : null
                }}
              />
            }
          />
          <Pie
            data={chartRows}
            dataKey="chart_value"
            nameKey="chartKey"
            isAnimationActive={false}
            innerRadius={68}
            outerRadius={96}
            paddingAngle={2}
            strokeWidth={2}
            labelLine
            label={renderPieShareLabel}
          >
            <Label
              content={({ viewBox }) => (
                <ReportPieCenterLabel
                  viewBox={viewBox}
                  totalValue={totalTaxCollected}
                  label="Tax Collected"
                />
              )}
            />
            {chartRows.map((row) => (
              // oxlint-disable-next-line typescript/no-deprecated -- grandfathered
              <Cell key={row.chartKey} fill={`var(--color-${row.chartKey})`} />
            ))}
          </Pie>
        </PieChart>
      </ChartContainer>
      <Typography variant="caption" align="center" className="mx-auto mt-3 block max-w-[220px]">
        Shows how tax activity is split across the jurisdictions in this report.
      </Typography>
    </div>
  )
}
